import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { CSSProperties } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, MinimalUserFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

export const AVATAR_SIZE = 24;
const NAME_MAX_WIDTH = 120;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment MinimalUser on User {
    id
    publicAddress
    webappUri
    username
    avatar {
      id
      url
    }
    artist {
      id
    }
  }
`);

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-2493/add-tests-for-minimaluser
export const MinimalUser = ({
  userFrag,
  hideAvatar = false,
  avatarSize = 24,
  verifiedSize = 20,
  borderRadius = 4,
  avatarHasBorder,
  maxWidth = NAME_MAX_WIDTH,
  gap,
  hideAvatarOnMobile = false,
  hideVerified = false,
  imagePriority,
}: {
  userFrag: FragmentType<MinimalUserFragmentDoc>;
  hideAvatar?: boolean;
  avatarSize?: number;
  verifiedSize?: number;
  borderRadius?: number;
  avatarHasBorder?: boolean;
  maxWidth?: CSSProperties['maxWidth'];
  gap?: CSSProperties['gap'];
  hideAvatarOnMobile?: boolean;
  hideVerified?: boolean;
  imagePriority: boolean;
}) => {
  const user = getFragment(MinimalUserFragmentDoc, userFrag);
  const { avatar, publicAddress, username, artist, webappUri } = user;

  const hoverCardTrigger = (
    <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
      <Container
        css={{
          gap: gap ? gap : '',
        }}
      >
        {!hideAvatar && (
          <AvatarWrapper hideAvatarOnMobile={hideAvatarOnMobile}>
            <Avatar
              src={avatar?.url}
              size={avatarSize}
              username={username}
              walletAddress={publicAddress}
              borderRadius={borderRadius}
              hasBorder={avatarHasBorder}
              webappUri={webappUri}
              scaleOnHover
              priority={imagePriority}
            />
          </AvatarWrapper>
        )}

        <Username className="username" css={{ maxWidth }}>
          {username}
        </Username>
      </Container>
    </AccessibleLink>
  );
  return (
    <UsernameWrapper>
      <UserHoverCard
        userHoverCardContent={<UserHoverCardContent userId={user.id} />}
        trigger={hoverCardTrigger}
        triggerAsChild
      />
      {artist && !hideVerified && <VerifiedTooltip portal size={verifiedSize} />}
    </UsernameWrapper>
  );
};

export const Container = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 4,

  '@tabletLandscape': {
    gap: 8,
  },
});

const AvatarWrapper = styled('div', {
  borderRadius: 4,
  flexShrink: 0,

  variants: {
    hideAvatarOnMobile: {
      true: {
        display: 'none',
        '@tablet': {
          display: 'block',
        },
      },
    },
  },
});

const UsernameWrapper = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
});

const Username = styled('h3', {
  base: 'S',
  fontStyle: 'semibold',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',

  '@tablet': {
    base: 'M',
  },

  '@tabletLandscape': {
    base: 'L',
  },
});
